// ============================================================================
// TREE
// ============================================================================

import CONFIG from '../config.js';
import { BreakableEntity } from './BreakableEntity.js';

export class Tree extends BreakableEntity {
    constructor(x, y, physics = null) {
        super(x, y, 30, 50, physics);
        this.shakeTime = 0;
        this.shakeAmplitude = 0;
        this.shakeAngle = 0;
        
        if (physics) {
            const radius = Math.max(this.width, this.height) / 2;
            this.body = physics.createCircle(x, y, radius, {
                isStatic: true,
                frictionAir: 0,
                friction: 0,
                restitution: 0
            });
            physics.linkEntity(this, this.body);
            
            // Override sensor: position at center of tree (same as collision body center)
            if (this.sensorBody) {
                physics.removeBody(this.sensorBody);
                this.sensorBody = null;
            }
            const sensorRadius = (CONFIG.interactionRadius || 50) * 0.8;
            this.sensorBody = physics.createSensor(x, y, sensorRadius); // Center
            physics.linkEntity(this, this.sensorBody);
        }
    }
    
    performAction(level, aggression, game) {
        this.shakeAmplitude = level * 3 + 1;
        this.shakeTime = 0.5 + level * 0.3;
        this.shakeAngle = Math.random() * Math.PI * 2;

        const leafCount = Math.floor((5 + aggression * 10) / 4);
        game.particles.emitLeaves(this.x, this.y, leafCount, null);

        game.camera.shake(CONFIG.shakeAmplitudes[level] * 0.5, CONFIG.shakeDurations[level] * 0.5);
    }

    getBreakThreshold() {
        return Infinity;
    }

    update(dt) {
        if (this.body) {
            this.x = this.body.position.x;
            this.y = this.body.position.y;
        }
        
        // Update sensor position to stay at center
        if (this.sensorBody && this.physics) {
            this.physics.setPosition(this.sensorBody, this.x, this.y);
        }
        
        if (this.shakeTime > 0) {
            this.shakeTime -= dt;
            if (this.shakeTime <= 0) {
                this.shakeAmplitude = 0;
            }
        }
    }

    draw(ctx, camera) {
        ctx.save();

        const drawX = this.x;
        const drawY = this.y;

        // Apply shake offset
        let shakeX = 0;
        let shakeY = 0;
        if (this.shakeTime > 0) {
            const shakeIntensity = this.shakeAmplitude * (this.shakeTime / 0.5);
            shakeX = Math.sin(this.shakeAngle + Date.now() / 50) * shakeIntensity;
            shakeY = Math.cos(this.shakeAngle + Date.now() / 50) * shakeIntensity * 0.5;
        }

        const finalX = drawX + shakeX;
        const finalY = drawY + shakeY;

        // --- 3/4 PERSPECTIVE: Tree trunk with depth ---
        const trunkHeight = this.height * 2 / 3;
        const trunkWidth = this.width / 2;
        const heightOffset = 20; // Trunk extends upward

        // Trunk side/thickness (darker)
        ctx.fillStyle = '#654321';
        ctx.fillRect(finalX - trunkWidth / 2, finalY - trunkHeight / 2 - heightOffset, trunkWidth, trunkHeight + heightOffset);

        // Trunk front face
        ctx.fillStyle = '#8B4513';
        ctx.fillRect(finalX - trunkWidth / 2, finalY - trunkHeight / 2 - heightOffset + 5, trunkWidth, trunkHeight);
        ctx.strokeStyle = '#654321';
        ctx.lineWidth = 2;
        ctx.strokeRect(finalX - trunkWidth / 2, finalY - trunkHeight / 2 - heightOffset + 5, trunkWidth, trunkHeight);

        // Foliage (circular, at top of trunk)
        const foliageY = finalY - this.height / 2 - heightOffset;
        const foliageRadius = this.width / 2;
        
        ctx.fillStyle = '#228B22';
        ctx.beginPath();
        ctx.arc(finalX, foliageY, foliageRadius, 0, Math.PI * 2);
        ctx.fill();
        
        ctx.strokeStyle = '#006400';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(finalX, foliageY, foliageRadius, 0, Math.PI * 2);
        ctx.stroke();

        // Texture highlights
        ctx.fillStyle = '#32CD32';
        ctx.beginPath();
        ctx.arc(finalX - 5, foliageY - 5, 8, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.arc(finalX + 5, foliageY - 3, 6, 0, Math.PI * 2);
        ctx.fill();

        ctx.restore();
    }
}
